import React from 'react';
import { DealStatusType, DEAL_STATUS_MAPPING } from 'components/common/StatusBadge/DealStatusBadge';
import { CloseCircleFilled, CheckCircleOutlined } from '@ant-design/icons';
import { Sourcing } from './icons/Sourcing';
import { Sales } from './icons/Sales';
import { Sourcing as SupplyOrder } from './icons/SupplyOrder';
import { Delivery } from './icons/Delivery';

interface DealProgressStatusProps {
  status: DealStatusType;
  width?: number;
  height?: number;
  circleSize?: number;
}

export const DealProgressStatus: React.FC<DealProgressStatusProps> = ({
  status,
  width = 500,
  height = 90,
  circleSize = 50,
}) => {
  const isRejected = status === 'REJECTED';

  // Define the order of statuses for the progress indicator
  const statusOrder: DealStatusType[] = isRejected
    ? ['SOURCING', 'SALES', 'SUPPLY_ORDER', 'DELIVERY', 'REJECTED']
    : ['SOURCING', 'SALES', 'SUPPLY_ORDER', 'DELIVERY', 'CLOSED'];

  // Map of status to icons
  const statusIcons: Record<DealStatusType, React.ReactNode> = {
    SOURCING: <Sourcing />,
    SALES: <Sales />,
    SUPPLY_ORDER: <SupplyOrder />,
    DELIVERY: <Delivery />,
    CLOSED: <CheckCircleOutlined style={{ color: '#99A0AE', fontSize: circleSize * 0.48 }} />,
    REJECTED: <CloseCircleFilled style={{ fontSize: circleSize * 0.48 }} />,
  };

  // Find the index of the current status in the status order
  const currentStatusIndex = isRejected ? 4 : statusOrder.findIndex((statusItem) => statusItem === status);

  // Calculate styles based on provided props
  const iconSize = circleSize * 0.6;
  const marginBottom = circleSize * 0.2;
  const lineTop = circleSize / 2;
  const maxStepWidth = width / statusOrder.length;

  return (
    <div style={{ padding: '20px 0', height }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: `${width}px`,
          position: 'relative',
          margin: '0',
        }}
      >
        {/* Connecting dashed lines */}
        <div
          style={{
            position: 'absolute',
            top: `${lineTop}px`,
            left: `${circleSize / 2 + 5}px`,
            width: `calc(100% - ${circleSize + 10}px)`,
            borderBottom: `2px dashed ${isRejected ? 'red' : '#DCE0E5'}`,
            zIndex: 1,
          }}
        />

        {/* Green dashed lines for completed steps */}
        {currentStatusIndex > 0 && !isRejected && (
          <div
            style={{
              position: 'absolute',
              top: `${lineTop}px`,
              left: `${circleSize / 2 + 5}px`,
              width: `calc((100% - ${circleSize + 10}px) * ${currentStatusIndex / (statusOrder.length - 1)})`,
              borderBottom: '2px dashed #05796B',
              zIndex: 2,
            }}
          />
        )}

        {/* Red dashed lines for rejected case */}
        {isRejected && (
          <div
            style={{
              position: 'absolute',
              top: `${lineTop}px`,
              left: `${circleSize / 2 + 5}px`,
              width: `calc(100% - ${circleSize + 10}px)`,
              borderBottom: '2px dashed red',
              zIndex: 2,
            }}
          />
        )}

        {/* Status steps */}
        {statusOrder.map((statusItem, index) => {
          const isActive = index === currentStatusIndex;
          const isPast = index < currentStatusIndex;
          const isFuture = index > currentStatusIndex;

          // Special styling for rejected status or when in rejected state
          const isRejectedStatus = statusItem === 'REJECTED';
          const isNextAfterActive = index === currentStatusIndex + 1;

          // Determine the appropriate styling based on status
          const circleBackground = isRejected
            ? '#fff2f0' // All circles have light red background when rejected
            : isRejectedStatus
              ? '#fff2f0'
              : !isRejected && (isActive || isPast)
                ? '#05796B'
                : '#fcfcfd';

          const circleBorder = isRejected
            ? '2px solid red' // All circles have red border when rejected
            : isRejectedStatus
              ? '2px solid red'
              : !isRejected && isPast
                ? '2px solid #05796B' // Past steps
                : !isRejected && isActive
                  ? '2px solid #05796B' // Active step
                  : !isRejected && isNextAfterActive
                    ? '2px dashed #05796B' // Next step after active
                    : '2px dashed #DCE0E5'; // Future steps (not next)

          // Icon color handling
          const iconColor = isRejected
            ? 'red' // All icons are red when rejected
            : isRejectedStatus
              ? 'red'
              : !isRejected && (isActive || isPast)
                ? 'white'
                : isNextAfterActive
                  ? '#05796B' // Next icon after active is green
                  : '#99A0AE'; // Other future icons remain grey

          // Special handling for Ant Design icons
          const isAntIcon = statusItem === 'CLOSED' || statusItem === 'REJECTED';

          return (
            <div
              key={statusItem}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 3,
                flex: 1,
                maxWidth: `${maxStepWidth}px`,
              }}
            >
              <div
                style={{
                  width: `${circleSize}px`,
                  height: `${circleSize}px`,
                  borderRadius: '50%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: circleBackground,
                  border: circleBorder,
                  marginBottom: `${marginBottom}px`,
                }}
              >
                <div
                  style={{
                    filter:
                      !isAntIcon && iconColor === 'white'
                        ? 'brightness(0) invert(1)'
                        : !isAntIcon && isNextAfterActive
                          ? 'invert(48%) sepia(51%) saturate(481%) hue-rotate(127deg) brightness(89%) contrast(91%)'
                          : 'none',
                    opacity: isFuture && !isNextAfterActive ? 0.5 : 1,
                    transform: 'scale(0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: `${iconSize}px`,
                    height: `${iconSize}px`,
                  }}
                >
                  {statusItem === 'CLOSED' ? (
                    <CheckCircleOutlined style={{ fontSize: circleSize * 0.48, color: iconColor }} />
                  ) : statusItem === 'REJECTED' ? (
                    <CloseCircleFilled style={{ fontSize: circleSize * 0.48, color: iconColor }} />
                  ) : (
                    statusIcons[statusItem]
                  )}
                </div>
              </div>
              <div
                style={{
                  color: isRejectedStatus ? 'red' : '#334155',
                  fontSize: '14px',
                  fontWeight: 500,
                  textAlign: 'center',
                  width: `${maxStepWidth - 10}px`,
                }}
              >
                {DEAL_STATUS_MAPPING[statusItem].label}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DealProgressStatus;
